import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/app/i18n/client';
import languageContext from '@/context/language';
import { ProductItem } from './product-item';
import { Spinner, Button, GlobalLoading } from '@/shared/components';
import Link from 'next/link';
import { useMutationReorder, useQueryMiniOrder, useQueryCancelStatus } from '@/hooks/orderhistory.hook';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import { usePriceFormat } from '@/hooks/use-price-format';
import { PrintContractButton } from '@/shared/pageComponents/print-contract';
import { checkIfFavorite } from '@/app/utils/check-favorite';
import { CURRENCY } from '@/app/utils/constant';
import { useCountry } from '@/hooks/use-country';
import { useEventTracking } from '@/shared/tracking/use-tracking';
import { usePlacement } from '@/shared/tracking/use-tracking-helpers';
import { SpinnerBox } from '@/shared/pageComponents/spinner-box';
import { OrderItem } from '@/types/order';
import { DispatchNotePdf } from '@/shared/pageComponents/dispatch-note-pdf';
import { InvoicePdf } from '@/shared/pageComponents/invoice-pdf';
import { useAppStore } from '@/store/app-store';

interface IProps {
  rowContent: { orderNumber: string; orderReferenceNo: string; poNumber: string };
  orderStatus?: string;
  dispatchT: any;
  invoiceT: any;
}
export default function OrderDetails(props: IProps) {
  const { isCN, isKR } = useCountry();
  const { configByCountry } = useAppStore();
  const { rowContent } = props;
  const { lang } = useContext(languageContext);
  const { t: commonT } = useLang(NAMESPACE.common);
  const { t } = useTranslation(lang, 'orderhistory');
  const getFormattedPrice = usePriceFormat();
  const gridClassName =
    'grid grid-cols-[100px_minmax(250px,_1fr)_1fr_120px_minmax(170px,_1fr)] grid-cols-[120px_minmax(250px,_1fr)_1fr_60px_150px] gap-x-2';
  const {
    data,
    isFetching,
    status,
    isSuccess: miniOrderIsSuccess,
  } = useQueryMiniOrder(rowContent.orderNumber, rowContent.orderReferenceNo);
  const { data: cancelOrderData } = useQueryCancelStatus({
    orderNumber: rowContent.orderNumber ? rowContent.orderNumber : '',
    PONumber: rowContent.poNumber,
    webOrderNumber: rowContent.orderReferenceNo ? rowContent.orderReferenceNo : '',
    isShowCancelOrder: configByCountry?.isShowCancelOrder,
  });
  const { mutate, isLoading: reorderLoading, isSuccess } = useMutationReorder();
  const router = useRouter();
  const [favoriteData, setFavoriteData] = useState<any>(null);
  const { trackEvent, trackEventEffect } = useEventTracking();
  const placement = usePlacement();
  const miniOrderData = data?.data;
  const reorder = (items: OrderItem[]) => {
    if (Array.isArray(items) && items.length > 0) {
      trackEvent({
        key: 'cart',
        action: 'addToCart',
        payload: {
          products: items.map((item) => {
            return {
              name: item?.catalogName,
              catalogId: item.catalogNumber,
              quantity: item.quantity,
              price: item.itemSubTotal,
              uom: item.unitSize,
              placement: 'order history',
              method: 'reorder',
            };
          }),
        },
      });
      mutate(items.map((item) => ({ catalogNumber: item.catalogNumber, quantity: item.quantity })));
    }
  };

  trackEventEffect(
    {
      key: 'productImpressions',
      action: 'productImpression',
      payload: miniOrderData?.orderItemDTOList?.map((item) => {
        return {
          name: item?.catalogName,
          catalogId: item?.catalogNumber,
          placement,
        };
      }),
    },
    miniOrderData?.orderItemDTOList && miniOrderData?.orderItemDTOList?.length > 0
  );

  useEffect(() => {
    if (isSuccess) {
      router.push('/cart');
      GlobalLoading.show();
      if (window.miniCart?.setCurrentQuantity) {
        window.miniCart?.setCurrentQuantity();
      }
    }
  }, [isSuccess]);

  useEffect(() => {
    const miniOrderItems = miniOrderData?.orderItemDTOList || [];
    if (miniOrderItems?.length > 0) {
      const catalogNumbersList = miniOrderItems.map((e) => e.catalogNumber).join(',');
      if (!favoriteData) {
        checkIfFavorite(catalogNumbersList).then((res) => {
          setFavoriteData(res);
        });
      }
    }
  }, [miniOrderData?.orderItemDTOList]);

  const orderDate = useMemo(() => {
    const date = miniOrderData?.orderDTO?.orderDate;
    return date ? dayjs(date).format('DD-MMM-YYYY') : '';
  }, [miniOrderIsSuccess]);

  const isOffline = useMemo(() => {
    return miniOrderData?.orderDTO?.orderSource === 'Offline';
  }, [miniOrderIsSuccess]);

  if (isFetching) {
    return (
      <div className="flex justify-center h-50 w-full gap-x-6">
        <div className="mx-auto my-6">
          <Spinner isLoading={true} />
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex justify-center h-50 w-full gap-x-6">
        <div className="mx-auto my-6">{t('miniorder.api.error')}</div>
      </div>
    );
  }

  if (!Array.isArray(miniOrderData?.orderItemDTOList) || miniOrderData?.orderItemDTOList?.length === 0) {
    return (
      <div className="flex justify-center h-50 w-full gap-x-6">
        <div className="mx-auto my-6">{t('miniorder.no.info')}</div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-[1fr_345px] min-h-[400px] w-full">
      <div className="flex bg-[#f7f7f7] px-[15px] py-5 col-span-2">
        <span className="pr-4">
          <span className="font-bold">{t('order.date')}:</span>
          <span className="pl-4">{orderDate}</span>
        </span>
        <span className="pr-4">
          <span className="font-bold">{t('purchase.order.number')}:</span>
          <span className="pl-4">{miniOrderData?.orderDTO?.purchaseOrderNumber}</span>
        </span>
        <span className="pr-4">
          <span className="font-bold">{t('order.refrence.no')}:</span>
          <span className="pl-4">{miniOrderData?.orderDTO?.webOrderNumber}</span>
        </span>
      </div>
      <div className="row-start-2 col-span-1 flex-1">
        <div className="py-4 px-5 my-4 flex flex-col border-0 border-r border-solid border-gray-450">
          <div
            className={`border-0 border-b border-solid border-gray-450 h-[42px] items-center text-sm text-gray-1100 font-bold ${gridClassName}`}
          >
            <span></span>
            <span>{t('item')}</span>
            <span>{t('item.status')}</span>
            <span className="justify-self-center">{commonT('quantity')}</span>
            <span className="justify-self-end">{t('price', {})}</span>
          </div>
          <div className="flex-1 min-h-[208px] flex flex-col">
            {miniOrderData?.orderItemDTOList
              ?.filter((item) => !item.freightLine)
              ?.map((item, index) => {
                return (
                  <ProductItem
                    key={item.catalogNumber + index}
                    index={index}
                    product={item}
                    orderNumber={rowContent.orderNumber}
                    poNumber={rowContent.poNumber}
                    isCancel={cancelOrderData?.data?.orderCancleItemList.some(
                      (cancelItem) => cancelItem.webOrderItemId === item.webOrderItemId
                    )}
                    isFavorite={
                      favoriteData?.find((favItem) => favItem.catalogNumber === item.catalogNumber)?.isFavorite
                    }
                  />
                );
              })}
          </div>
        </div>
      </div>
      <div className="flex-shrink-0 h-auto row-start-2 col-span-1 m-5">
        <h5 className="mb-5">{t('order.summary')}</h5>
        <div>
          {isKR && (
            <>
              <div className="pr-4 flex">
                <span>{t('total.items')}:</span>
                <span className="pl-4 ml-auto">{miniOrderData?.orderDTO?.totalItems}</span>
              </div>
              <div className="pr-4 flex">
                <span className="font-bold">{commonT('subtotal')}:</span>
                <span className="pl-4 ml-auto">{getFormattedPrice(miniOrderData?.orderDTO?.subTotal)}</span>
              </div>
              {!isOffline && (
                <div className="pr-4 flex">
                  <span>{t('vat')}:</span>
                  <span className="pl-4 ml-auto">{getFormattedPrice.roundVat(miniOrderData?.orderDTO?.vatCharge)}</span>
                </div>
              )}
            </>
          )}
          <div className="pr-4 mt-6 flex text-lg">
            <span className="font-semibold">
              {t('total')} ({CURRENCY}):
            </span>
            <span className="pl-4 ml-auto font-light">
              {CURRENCY}&nbsp;
              <span className="font-semibold">{getFormattedPrice(miniOrderData?.orderDTO?.totalPrice)}</span>
            </span>
          </div>
          {isCN && !isOffline && miniOrderData?.orderDTO?.freightCharge > 0 && (
            <div className="pr-4 flex font-light">
              <span>{t('freight.charge')}:</span>
              <span className="pl-4 ml-auto">{getFormattedPrice(miniOrderData?.orderDTO?.freightCharge)}</span>
            </div>
          )}
        </div>
        <div className="w-full mt-8">
          <Link
            prefetch
            href={{
              pathname: '/order-detail',
              query: {
                orderNumber: rowContent.orderNumber,
                webOrderNumber: rowContent.orderReferenceNo,
              },
            }}
          >
            <Button type="outline" className="p-0 w-full">
              {t('view.order.details')}
            </Button>
          </Link>
          <div className="grid grid-cols-2 gap-x-4">
            <DispatchNotePdf
              className="mt-5"
              orderNumber={miniOrderData?.orderDTO?.e1OrderNumber}
              status={miniOrderData?.orderDTO?.searchStatus}
            >
              {props?.dispatchT('dispatch.note')}
            </DispatchNotePdf>
            <InvoicePdf
              className="mt-5"
              orderNumber={miniOrderData?.orderDTO?.e1OrderNumber}
              status={miniOrderData?.orderDTO?.searchStatus}
            >
              {props?.invoiceT('invoice')}
            </InvoicePdf>
          </div>
          {isCN && !isOffline && (
            <PrintContractButton
              disabled={(miniOrderData?.orderDTO?.searchStatus ?? props.orderStatus) === 'Cancelled'}
              orderNumber={rowContent.orderNumber ? rowContent.orderNumber : rowContent.orderReferenceNo}
              className="p-0 w-full mt-5"
            >
              {t('print.contract')}
            </PrintContractButton>
          )}
          {!isOffline && (
            <SpinnerBox isLoading={reorderLoading}>
              <Button
                type="outline"
                className="p-0 w-full mt-5"
                label={t('reorder')}
                onClick={() => reorder(miniOrderData?.orderItemDTOList)}
              ></Button>
            </SpinnerBox>
          )}
        </div>
      </div>
    </div>
  );
}
